import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Logo } from "@/components/Logo";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(form.email, form.password);
      const admin = ["super_admin", "national_admin", "diocesan_admin", "community_coordinator", "store_manager"].includes(user.role);
      navigate(admin ? "/admin" : "/o-meu-terco");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-th-blue th-pattern py-16 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-6"><Logo size={54} className="mx-auto" /><h1 className="mt-4 font-display font-bold text-2xl text-th-blue">{t("login_title")}</h1></div>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div><Label>{t("form_email")}</Label><Input required type="email" value={form.email} data-testid="login-email" onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
          <div><Label>{t("password")}</Label><PasswordInput required value={form.password} testid="login-password" onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
          <div className="text-right -mt-1"><Link to="/recuperar-palavra-passe" className="text-xs text-slate-500 hover:text-th-blue" data-testid="link-forgot">{t("forgot_password")}</Link></div>
          {error && <div className="text-red-600 text-sm bg-red-50 rounded p-2" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-th-blue text-white hover:bg-th-bluelight h-11">{loading ? t("loading") : t("login_title")}</Button>
        </form>
        <div className="my-5 flex items-center gap-3 text-slate-300">
          <div className="flex-1 h-px bg-slate-200" /><span className="text-xs uppercase tracking-wide">{t("login_or")}</span><div className="flex-1 h-px bg-slate-200" />
        </div>
        <Button type="button" onClick={loginWithGoogle} data-testid="login-google" variant="outline" className="w-full h-11 border-slate-300 text-th-blue hover:bg-slate-50 gap-2">
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
          {t("login_google")}
        </Button>
        <p className="mt-5 text-center text-sm text-slate-500">{t("no_account")} <Link to="/registar" className="text-th-blue font-semibold hover:text-th-gold" data-testid="link-register">{t("register_title")}</Link></p>
      </div>
    </div>
  );
}
